import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './css/Maps.css';
import OpenStatus, { computeOpenStatus } from './OpenStatus';

// Default Leaflet icon fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
});

// Custom status icons
const redIcon = new L.Icon({
  iconUrl: '/gray.png',
  iconSize: [40, 40],
  iconAnchor: [12, 41],
  popupAnchor: [8, -37],
});

const greenIcon = new L.Icon({
  iconUrl: '/blue.png',
  iconSize: [40, 40],
  iconAnchor: [12, 41],
  popupAnchor: [8, -37],
});

const grayIcon = new L.Icon({
  iconUrl: '/gray.png',
  iconSize: [40, 40],
  iconAnchor: [12, 41],
  popupAnchor: [8, -37],
});

const userIcon = new L.Icon({
  iconUrl: '/stickman.png',
  iconSize: [60, 60],
  iconAnchor: [12, 41],
  popupAnchor: [19, -37],
});

function Maps({ places }) {
  const [userLocation, setUserLocation] = useState(null);

  // Normalize places into an array and memoize so we don't recreate it every render
  const placesArray = useMemo(() => (Array.isArray(places) ? places : places ? [places] : []), [places]);

  // Get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        () => setUserLocation({ lat: 48.1486, lng: 17.1077 }) // fallback Bratislava
      );
    } else {
      setUserLocation({ lat: 48.1486, lng: 17.1077 });
    }
  }, []);

  // Map center logic — memoized to avoid passing a new array each render
  const mapCenter = useMemo(() => {
    if (placesArray.length === 1) return [placesArray[0].latitude, placesArray[0].longitude];
    if (userLocation) return [userLocation.lat, userLocation.lng];
    return [48.1486, 17.1077];
  }, [placesArray, userLocation]);

  // Compute status for each place once per relevant change (uses pure helper)
  const placesWithStatus = useMemo(() => {
    return placesArray.map((place) => {
      const statusObj = computeOpenStatus(place.opening_hours);
      return { ...place, _statusObj: statusObj };
    });
  }, [placesArray]);

  return (
    <div className="map-container">
      <div className="map-overlay"></div>
      {(userLocation || placesArray.length === 1) && (
        <MapContainer className="map" center={mapCenter} zoom={15} scrollWheelZoom={true} zoomControl={false}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap contributors"
          />

          {/* User location marker always on top */}
          {userLocation && (
            <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon} zIndexOffset={500}>
              <Popup>Vaša poloha</Popup>
            </Marker>
          )}

          {/* Place markers (memoized list) */}
          {placesWithStatus.map((place) => {
            if (!place.latitude || !place.longitude) return null;

            let markerIcon;
            let zIndexOffset = 0;

            if (placesArray.length === 1) {
              markerIcon = greenIcon; // single place → blue
              zIndexOffset = 300;
            } else {
              const statusData = place._statusObj?.sortStatus;
              if (statusData === 'open') {
                markerIcon = greenIcon;
                zIndexOffset = 300;
              } else if (statusData === 'closed') {
                markerIcon = redIcon;
                zIndexOffset = 200;
              } else {
                markerIcon = grayIcon;
                zIndexOffset = 100;
              }
            }

            return (
              <Marker key={place.place_id} position={[place.latitude, place.longitude]} icon={markerIcon} zIndexOffset={zIndexOffset}>
                <Popup>
                  <Link id="place-name" to={`/catalog/${place.place_id}`}>
                    <strong>{place.name}</strong>
                  </Link>
                  <br />

                  <Link id="place-address" to={place.google_maps_url}>
                    {place.address}
                  </Link>

                  {/* {place.phone_number && <a href={`tel:${place.phone_number}`}>Zavolať</a>} */}
                  <OpenStatus openingHours={place.opening_hours} />
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      )}
      {!userLocation && placesArray.length !== 1 && <p>Loading map...</p>}
    </div>
  );
}

// Shallow comparison for places prop to avoid re-rendering when array identity changes but contents are the same
const shallowPlacesEqual = (prevProps, nextProps) => {
  const a = prevProps.places;
  const b = nextProps.places;
  if (!a && !b) return true;
  if (!a || !b) return false;

  // If not arrays, compare as single-place objects
  const arrA = Array.isArray(a) ? a : [a];
  const arrB = Array.isArray(b) ? b : [b];
  if (arrA.length !== arrB.length) return false;

  for (let i = 0; i < arrA.length; i++) {
    const pa = arrA[i];
    const pb = arrB[i];
    if (!pa || !pb) return false;
    if (pa.place_id !== pb.place_id) return false;
    if (pa.latitude !== pb.latitude || pa.longitude !== pb.longitude) return false;
    // quick guard for opening hours change (string/length)
    const oa = pa.opening_hours;
    const ob = pb.opening_hours;
    if (!!oa !== !!ob) return false;
    if (oa && ob) {
      if (Array.isArray(oa) && Array.isArray(ob)) {
        if (oa.length !== ob.length) return false;
      } else if (String(oa) !== String(ob)) return false;
    }
  }

  return true;
};

export default React.memo(Maps, shallowPlacesEqual);
